import React, { useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Box,
  Button,
  Container,
  Flex,
  Heading,
  HStack,
  Image,
  SimpleGrid,
  Spinner,
  Stack,
  Text,
  Icon,
  Wrap,
  WrapItem,
} from '@chakra-ui/react';
import { Link as RouterLink, useLocation, useParams } from 'react-router-dom';
import Helmet from 'react-helmet';
import {
  buildProposalUiModel,
  getMediaUrl,
} from '../components/proposals-school/proposalsSchoolUi';
import { resolvePublicProposal } from '../utils/resolvePublicProposal';
import {
  getThemeFontImport,
  resolveProposalTheme,
  resolveProposalThemeKeyFromPath,
} from '../components/proposals-standalone/proposalThemes';

/** Lista proposte della stessa landing + anchor sulla sezione elenco (scroll). */
const resolveProposteBreadcrumbHref = (pathname) => {
  if (!pathname) return '/proposte-scuole#proposte-scuole';
  if (/^\/proposte-scuole\/[^/]+$/.test(pathname)) {
    return '/proposte-scuole#proposte-scuole';
  }
  if (/^\/eventi-adulti\/proposte\/[^/]+$/.test(pathname)) {
    return '/eventi-adulti#eventi-adulti';
  }
  if (/^\/magic-tcg\/proposte\/[^/]+$/.test(pathname)) {
    return '/magic-tcg#magic-tcg';
  }
  if (/^\/proposte-famiglie\/proposte\/[^/]+$/.test(pathname)) {
    return '/proposte-famiglie#proposte-famiglie';
  }
  if (/^\/proposte-ragazzi\/proposte\/[^/]+$/.test(pathname)) {
    return '/proposte-ragazzi#proposte-ragazzi';
  }
  return '/proposte-scuole#proposte-scuole';
};

/** Coerenza URL dettaglio ↔ publicSubtype restituito dall'API */
const getExpectedPublicSubtypesForPath = (pathname) => {
  if (/^\/proposte-scuole\/[^/]+$/.test(pathname)) return ['school'];
  if (/^\/eventi-adulti\/proposte\/[^/]+$/.test(pathname)) return ['adults'];
  if (/^\/magic-tcg\/proposte\/[^/]+$/.test(pathname)) return ['magic'];
  if (/^\/proposte-famiglie\/proposte\/[^/]+$/.test(pathname))
    return ['family'];
  if (/^\/proposte-ragazzi\/proposte\/[^/]+$/.test(pathname)) {
    return ['kids', 'teens'];
  }
  return null;
};

const buildPrimaryCta = (proposal) => {
  const ctaUrl = resolvePublicProposal(proposal).cta?.url || '';
  if (ctaUrl) {
    const isAbsolute = /^https?:\/\//i.test(ctaUrl);
    return {
      label: proposal?.ctaLabel || 'Richiedi informazioni',
      ...(isAbsolute
        ? { href: ctaUrl, isExternal: true }
        : { to: ctaUrl, isExternal: false }),
    };
  }
  return {
    label: proposal?.ctaLabel || 'Richiedi informazioni',
    to: '/contattaci',
    isExternal: false,
  };
};

export default function PropostaPubblicaDetailPage() {
  const { slug } = useParams();
  const { pathname } = useLocation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [proposal, setProposal] = useState(null);
  const [shareCopied, setShareCopied] = useState(false);
  const theme = useMemo(
    () => resolveProposalTheme(resolveProposalThemeKeyFromPath(pathname)),
    [pathname]
  );
  const t = theme.colors;

  useEffect(() => {
    const loadProposal = async () => {
      setLoading(true);
      setError('');
      try {
        const response = await fetch(`/api/proposte-pubbliche/${slug}`);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const payload = await response.json();
        const data = payload?.data || null;
        const expected = getExpectedPublicSubtypesForPath(pathname);
        const effectiveSubtype = data?.publicSubtype || 'school';
        if (data && expected && !expected.includes(effectiveSubtype)) {
          setProposal(null);
          setError('Questa proposta non è disponibile in questa sezione.');
        } else {
          setProposal(data);
        }
      } catch (err) {
        setError('Proposta non trovata o non disponibile.');
      } finally {
        setLoading(false);
      }
    };

    if (slug) loadProposal();
  }, [slug, pathname]);

  const isSchoolPage = /^\/proposte-scuole(\/|$)/.test(pathname);

  const meta = useMemo(() => {
    if (!proposal) return {};
    return buildProposalUiModel(proposal, {
      audience: isSchoolPage ? 'school' : 'default',
    });
  }, [proposal, isSchoolPage]);

  const handleShare = async () => {
    if (typeof window === 'undefined') return;
    const shareUrl = window.location.href;
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(shareUrl);
      } else {
        const input = document.createElement('input');
        input.value = shareUrl;
        document.body.appendChild(input);
        input.select();
        document.execCommand('copy');
        document.body.removeChild(input);
      }
      setShareCopied(true);
      window.setTimeout(() => setShareCopied(false), 1800);
    } catch (_) {
      setShareCopied(false);
    }
  };

  const documents = useMemo(() => {
    const documentItems = Array.isArray(meta.documents) ? meta.documents : [];
    const galleryItems = Array.isArray(meta.gallery) ? meta.gallery : [];
    const sourceItems = documentItems.length ? documentItems : galleryItems;
    const imageExtensions = new Set([
      'jpg',
      'jpeg',
      'png',
      'gif',
      'webp',
      'svg',
      'bmp',
      'avif',
    ]);
    return sourceItems.filter((item) => {
      const mime =
        typeof item?.mime === 'string'
          ? item.mime
          : typeof item?.attributes?.mime === 'string'
            ? item.attributes.mime
            : '';
      const url = getMediaUrl(item);
      if (!url) return false;

      if (mime) return !mime.startsWith('image/');

      const rawName = String(
        item?.name ||
          item?.attributes?.name ||
          item?.alternativeText ||
          item?.url ||
          ''
      ).toLowerCase();
      const ext = rawName.includes('.') ? rawName.split('.').pop() : '';
      if (!ext) return true;
      return !imageExtensions.has(ext);
    });
  }, [meta.documents, meta.gallery]);

  const extraPricingRules = useMemo(() => {
    const rules = Array.isArray(meta.pricingRules) ? meta.pricingRules : [];
    return rules.slice(1);
  }, [meta.pricingRules]);
  const primaryPricingRule = useMemo(() => {
    const rules = Array.isArray(meta.pricingRules) ? meta.pricingRules : [];
    return rules[0] || null;
  }, [meta.pricingRules]);

  const hasPracticalInfoSection = useMemo(() => {
    if (!proposal) return false;
    const m = meta;
    const text = (v) => typeof v === 'string' && v.trim().length > 0;
    return Boolean(
      m.materialsIncluded ||
        text(m.materials) ||
        text(m.whatToBring) ||
        (Array.isArray(m.skills) && m.skills.length) ||
        (Array.isArray(m.benefits) && m.benefits.length) ||
        text(m.safetyNotes) ||
        text(m.weatherFallback) ||
        text(m.accessibility) ||
        text(m.notes) ||
        (!isSchoolPage && m.parentsRequired) ||
        (!isSchoolPage && m.strollerFriendly) ||
        m.bookingRequired ||
        m.privateGroupAvailable
    );
  }, [isSchoolPage, meta, proposal]);

  const formatPricingRule = (rule) => {
    const mode = rule?.mode || 'per_incontro';
    if (mode === 'per_persona') {
      const amount = rule?.unitAmount || rule?.amount;
      return amount ? `${amount} EUR / persona` : 'Costo a persona';
    }
    if (mode === 'per_pacchetto') {
      const amount = rule?.amount || rule?.unitAmount;
      if (!amount) return 'Costo a pacchetto';
      const meetings =
        Number.isFinite(Number(rule?.packageMeetings)) &&
        Number(rule.packageMeetings) > 0
          ? ` (${Number(rule.packageMeetings)} incontri)`
          : '';
      return `${amount} EUR / pacchetto${meetings}`;
    }
    if (mode === 'per_gita') {
      const amount = rule?.amount || rule?.unitAmount;
      return amount ? `${amount} EUR / gita` : 'Costo a gita';
    }
    if (mode === 'per_totale') {
      const amount = rule?.amount || rule?.unitAmount;
      return amount ? `${amount} EUR` : 'Costo totale';
    }
    const amount = rule?.amount || rule?.unitAmount;
    return amount ? `${amount} EUR / incontro` : 'Costo a incontro';
  };

  return (
    <Box bg={t.bg} minH="100vh" py={{ base: 10, md: 14 }}>
      <Helmet>
        <title>{meta?.title || 'Dettaglio proposta'} - CIL</title>
        <link rel="stylesheet" href={getThemeFontImport(theme.key)} />
      </Helmet>
      <Container maxW="container.xl">
        <Stack spacing={6}>
          {loading ? (
            <Stack direction="row" align="center">
              <Spinner color="#36a9e1" />
              <Text>Caricamento dettaglio...</Text>
            </Stack>
          ) : null}

          {!loading && error ? (
            <Box
              bg="orange.50"
              borderWidth="1px"
              borderColor="orange.200"
              p={5}
              borderRadius="lg"
            >
              <Text>{error}</Text>
            </Box>
          ) : null}

          {!loading && !error && proposal ? (
            <Stack
              spacing={5}
              bg={t.surface}
              borderWidth="2px"
              borderColor={t.border}
              borderRadius="2xl"
              p={{ base: 5, md: 8 }}
              boxShadow="0 18px 36px rgba(0, 0, 0, 0.12)"
            >
              <Stack spacing={3} align="center" textAlign="center">
                <Breadcrumb
                  separator="›"
                  color={t.muted}
                  fontWeight="700"
                  w="100%"
                  display="flex"
                  justifyContent="center"
                >
                  <BreadcrumbItem>
                    <BreadcrumbLink
                      as={RouterLink}
                      to={resolveProposteBreadcrumbHref(pathname)}
                      color={t.accentAlt}
                    >
                      {isSchoolPage ? 'Proposte scuole' : 'Proposte'}
                    </BreadcrumbLink>
                  </BreadcrumbItem>
                  <BreadcrumbItem isCurrentPage>
                    <Text
                      as="span"
                      color={t.text}
                      fontWeight="700"
                      noOfLines={2}
                    >
                      {meta.title || 'Dettaglio'}
                    </Text>
                  </BreadcrumbItem>
                </Breadcrumb>
              </Stack>

              {meta.heroImageUrl ? (
                <Box w="100%">
                  <Image
                    src={meta.heroImageUrl}
                    alt={meta.title}
                    borderRadius="28px"
                    h={{ base: '220px', md: '420px' }}
                    w="100%"
                    objectFit="cover"
                  />
                </Box>
              ) : null}

              <Stack spacing={3} align="center" textAlign="center">
                <Heading
                  as="h1"
                  size="2xl"
                  color={t.text}
                  fontFamily={theme.fontHeading}
                >
                  {meta.title}
                </Heading>
                <Text fontSize="lg" color={t.muted} fontFamily={theme.fontBody}>
                  {meta.shortDescription || 'Descrizione non disponibile.'}
                </Text>
              </Stack>

              {meta.detailItems?.length ? (
                <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
                  {meta.detailItems.map((detail) => {
                    const iconBox = (
                      <Box
                        w="48px"
                        h="48px"
                        borderRadius="16px"
                        bg={t.accent}
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        flexShrink={0}
                      >
                        <Icon as={detail.icon} color={t.text} />
                      </Box>
                    );
                    const labelText = (
                      <Text
                        fontSize={{ base: 'sm', md: 'xs' }}
                        textTransform="uppercase"
                        letterSpacing="0.04em"
                        color={t.muted}
                        fontWeight="800"
                        flexShrink={0}
                      >
                        {detail.label}
                      </Text>
                    );
                    const valueBlock = Array.isArray(detail.value) ? (
                      <Stack spacing={1}>
                        {detail.value.map((entry) => (
                          <Text
                            key={`${detail.key}-${entry}`}
                            fontSize={{ base: 'lg', md: 'md' }}
                            color={t.text}
                            fontWeight="800"
                          >
                            {entry}
                          </Text>
                        ))}
                      </Stack>
                    ) : detail.key === 'price' ? (
                      <Stack spacing={2}>
                        {primaryPricingRule ? (
                          <Text
                            fontSize={{ base: 'lg', md: 'md' }}
                            color={t.text}
                            fontWeight="700"
                          >
                            {primaryPricingRule.notes ||
                            primaryPricingRule.label
                              ? `${primaryPricingRule.notes || primaryPricingRule.label}: ${formatPricingRule(primaryPricingRule)}`
                              : formatPricingRule(primaryPricingRule)}
                          </Text>
                        ) : (
                          <Text
                            fontSize={{ base: 'lg', md: 'md' }}
                            color={t.text}
                            fontWeight="700"
                          >
                            {detail.value}
                          </Text>
                        )}
                        {extraPricingRules.map((rule) => (
                          <Text
                            key={`inline-extra-pricing-${rule.mode || 'mode'}-${rule.label || 'label'}-${rule.amount || 'amount'}-${rule.unitAmount || 'unit'}-${rule.packageMeetings || 'pkg'}-${rule.notes || 'notes'}`}
                            fontSize={{ base: 'lg', md: 'md' }}
                            color={t.text}
                            fontWeight="700"
                          >
                            {rule.notes || rule.label
                              ? `${rule.notes || rule.label}: ${formatPricingRule(rule)}`
                              : formatPricingRule(rule)}
                          </Text>
                        ))}
                      </Stack>
                    ) : (
                      <Text
                        fontSize={{ base: 'xl', md: 'lg' }}
                        color={t.text}
                        fontWeight="900"
                      >
                        {detail.value}
                      </Text>
                    );

                    return (
                      <Box
                        key={detail.key}
                        bg={t.surface}
                        borderRadius="24px"
                        p={5}
                        boxShadow="0 12px 28px rgba(0, 0, 0, 0.1)"
                        border="2px solid"
                        borderColor={t.border}
                      >
                        <Flex align="center" gap={3} flexWrap="wrap" w="100%">
                          {iconBox}
                          {labelText}
                          <Box
                            flex={{ base: '1 1 100%', md: '1 1 0%' }}
                            minW={0}
                          >
                            {valueBlock}
                          </Box>
                        </Flex>
                      </Box>
                    );
                  })}
                </SimpleGrid>
              ) : null}

              {meta.keywords?.length ? (
                <Box textAlign="center" mb={4}>
                  <Heading
                    as="h2"
                    size="md"
                    mb={3}
                    color={t.text}
                    fontFamily={theme.fontHeading}
                  >
                    Parole chiave
                  </Heading>
                  <Stack
                    direction="row"
                    flexWrap="wrap"
                    gap={2}
                    justify="center"
                  >
                    {meta.keywords.map((keyword) => (
                      <Badge
                        key={keyword}
                        bg="rgba(54, 169, 225, 0.14)"
                        color={t.text}
                        borderRadius="999px"
                        px={4}
                        py={2}
                        fontWeight="700"
                      >
                        {keyword}
                      </Badge>
                    ))}
                  </Stack>
                </Box>
              ) : null}

              {meta.fullDescription ? (
                <Text
                  whiteSpace="pre-wrap"
                  color={t.muted}
                  fontFamily={theme.fontBody}
                >
                  {meta.fullDescription}
                </Text>
              ) : null}

              {hasPracticalInfoSection ? (
                <Box
                  borderWidth="1px"
                  borderColor={t.border}
                  borderRadius="2xl"
                  p={{ base: 5, md: 7 }}
                  bg={t.surface}
                >
                  <Heading
                    as="h2"
                    size="md"
                    mb={4}
                    color={t.text}
                    fontFamily={theme.fontHeading}
                    textAlign="center"
                  >
                    Informazioni pratiche
                  </Heading>
                  <Stack spacing={4} textAlign="left">
                    {meta.materialsIncluded ||
                    (meta.materials && meta.materials.trim()) ? (
                      <Box>
                        <Text
                          fontSize="xs"
                          textTransform="uppercase"
                          letterSpacing="0.06em"
                          color={t.muted}
                          fontWeight="800"
                          mb={1}
                        >
                          Materiali
                        </Text>
                        {meta.materialsIncluded ? (
                          <Text color={t.text} fontWeight="700" mb={1}>
                            Materiali inclusi nell&apos;offerta
                          </Text>
                        ) : null}
                        {meta.materials?.trim() ? (
                          <Text
                            whiteSpace="pre-wrap"
                            color={t.muted}
                            fontFamily={theme.fontBody}
                          >
                            {meta.materials.trim()}
                          </Text>
                        ) : null}
                      </Box>
                    ) : null}

                    {meta.whatToBring?.trim() ? (
                      <Box>
                        <Text
                          fontSize="xs"
                          textTransform="uppercase"
                          letterSpacing="0.06em"
                          color={t.muted}
                          fontWeight="800"
                          mb={1}
                        >
                          Cosa portare
                        </Text>
                        <Text
                          whiteSpace="pre-wrap"
                          color={t.muted}
                          fontFamily={theme.fontBody}
                        >
                          {meta.whatToBring.trim()}
                        </Text>
                      </Box>
                    ) : null}

                    {meta.skills?.length ? (
                      <Box>
                        <Text
                          fontSize="xs"
                          textTransform="uppercase"
                          letterSpacing="0.06em"
                          color={t.muted}
                          fontWeight="800"
                          mb={2}
                        >
                          Competenze / abilità
                        </Text>
                        <Wrap spacing={2}>
                          {meta.skills.map((s) => (
                            <WrapItem key={s}>
                              <Badge
                                borderRadius="full"
                                px={3}
                                py={1}
                                bg="rgba(54, 169, 225, 0.14)"
                                color={t.text}
                                fontWeight="700"
                              >
                                {s}
                              </Badge>
                            </WrapItem>
                          ))}
                        </Wrap>
                      </Box>
                    ) : null}

                    {meta.benefits?.length ? (
                      <Box>
                        <Text
                          fontSize="xs"
                          textTransform="uppercase"
                          letterSpacing="0.06em"
                          color={t.muted}
                          fontWeight="800"
                          mb={2}
                        >
                          Benefici
                        </Text>
                        <Stack spacing={1}>
                          {meta.benefits.map((b) => (
                            <Text key={b} color={t.text} fontWeight="600">
                              {b}
                            </Text>
                          ))}
                        </Stack>
                      </Box>
                    ) : null}

                    {meta.safetyNotes?.trim() ? (
                      <Box>
                        <Text
                          fontSize="xs"
                          textTransform="uppercase"
                          letterSpacing="0.06em"
                          color={t.muted}
                          fontWeight="800"
                          mb={1}
                        >
                          Sicurezza
                        </Text>
                        <Text
                          whiteSpace="pre-wrap"
                          color={t.muted}
                          fontFamily={theme.fontBody}
                        >
                          {meta.safetyNotes.trim()}
                        </Text>
                      </Box>
                    ) : null}

                    {meta.weatherFallback?.trim() ? (
                      <Box>
                        <Text
                          fontSize="xs"
                          textTransform="uppercase"
                          letterSpacing="0.06em"
                          color={t.muted}
                          fontWeight="800"
                          mb={1}
                        >
                          Alternativa in caso di maltempo
                        </Text>
                        <Text
                          whiteSpace="pre-wrap"
                          color={t.muted}
                          fontFamily={theme.fontBody}
                        >
                          {meta.weatherFallback.trim()}
                        </Text>
                      </Box>
                    ) : null}

                    {meta.accessibility?.trim() ? (
                      <Box>
                        <Text
                          fontSize="xs"
                          textTransform="uppercase"
                          letterSpacing="0.06em"
                          color={t.muted}
                          fontWeight="800"
                          mb={1}
                        >
                          Accessibilità
                        </Text>
                        <Text color={t.muted} fontFamily={theme.fontBody}>
                          {meta.accessibility.trim()}
                        </Text>
                      </Box>
                    ) : null}

                    {meta.notes?.trim() ? (
                      <Box>
                        <Text
                          fontSize="xs"
                          textTransform="uppercase"
                          letterSpacing="0.06em"
                          color={t.muted}
                          fontWeight="800"
                          mb={1}
                        >
                          Note operative
                        </Text>
                        <Text
                          whiteSpace="pre-wrap"
                          color={t.muted}
                          fontFamily={theme.fontBody}
                        >
                          {meta.notes.trim()}
                        </Text>
                      </Box>
                    ) : null}

                    {(!isSchoolPage && meta.parentsRequired) ||
                    (!isSchoolPage && meta.strollerFriendly) ||
                    meta.bookingRequired ||
                    meta.privateGroupAvailable ? (
                      <Stack spacing={1}>
                        <Text
                          fontSize="xs"
                          textTransform="uppercase"
                          letterSpacing="0.06em"
                          color={t.muted}
                          fontWeight="800"
                        >
                          Organizzazione
                        </Text>
                        {!isSchoolPage && meta.parentsRequired ? (
                          <Text color={t.text} fontWeight="700">
                            Presenza di un adulto richiesta
                          </Text>
                        ) : null}
                        {!isSchoolPage && meta.strollerFriendly ? (
                          <Text color={t.text} fontWeight="700">
                            Percorso accessibile con passeggino
                          </Text>
                        ) : null}
                        {meta.bookingRequired ? (
                          <Text color={t.text} fontWeight="700">
                            Prenotazione obbligatoria
                          </Text>
                        ) : null}
                        {meta.privateGroupAvailable ? (
                          <Text color={t.text} fontWeight="700">
                            Disponibile per gruppi privati
                          </Text>
                        ) : null}
                      </Stack>
                    ) : null}
                  </Stack>
                </Box>
              ) : null}

              {meta.objectives?.length ? (
                <Box textAlign="center">
                  <Heading
                    as="h2"
                    size="md"
                    mb={3}
                    color={t.text}
                    fontFamily={theme.fontHeading}
                  >
                    {theme.key === 'magic'
                      ? 'Perché ci piace'
                      : 'Cosa sviluppa'}
                  </Heading>
                  <Flex flexWrap="wrap" gap={3} justify="center" align="center">
                    {meta.objectives.map((objective) => (
                      <Box
                        key={objective}
                        bg={t.accentAlt}
                        color="#fff"
                        borderRadius="16px"
                        px={4}
                        py={3}
                        fontSize="sm"
                        fontWeight="700"
                        textAlign="center"
                        maxW={{ base: '100%', sm: '320px' }}
                      >
                        {objective}
                      </Box>
                    ))}
                  </Flex>
                </Box>
              ) : null}

              {documents.length ? (
                <Box>
                  <Heading
                    as="h2"
                    size="md"
                    mb={3}
                    color={t.text}
                    fontFamily={theme.fontHeading}
                  >
                    Documentazione
                  </Heading>
                  <Stack spacing={3}>
                    {documents.map((item) => {
                      const fileUrl = getMediaUrl(item);
                      if (!fileUrl) return null;
                      const fileName =
                        item?.name ||
                        item?.attributes?.name ||
                        item?.alternativeText ||
                        'Documento';
                      return (
                        <HStack
                          key={`document-${item.id || item.documentId || item.url}`}
                          justify="space-between"
                          bg={t.surface}
                          borderRadius="16px"
                          border="1px solid"
                          borderColor={t.border}
                          px={4}
                          py={3}
                        >
                          <Text color={t.text} fontWeight="700">
                            {fileName}
                          </Text>
                          <Button
                            as="a"
                            href={fileUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            download
                            size="sm"
                            borderRadius="999px"
                            bg={t.accentAlt}
                            color="#fff"
                            _hover={{ opacity: 0.92 }}
                          >
                            Scarica
                          </Button>
                        </HStack>
                      );
                    })}
                  </Stack>
                </Box>
              ) : null}

              {meta.gallery.length ? (
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                  {meta.gallery.slice(0, 4).map((item) => {
                    const url = getMediaUrl(item);
                    const isImage =
                      typeof item?.mime === 'string' &&
                      item.mime.startsWith('image/');
                    if (!isImage) return null;
                    return (
                      <Image
                        key={`gallery-${item.id || item.documentId || item.url}`}
                        src={url}
                        alt={
                          item?.alternativeText ||
                          item?.name ||
                          'Immagine proposta'
                        }
                        borderRadius="28px"
                        h={{ base: '220px', md: '280px' }}
                        w="100%"
                        objectFit="cover"
                      />
                    );
                  })}
                </SimpleGrid>
              ) : null}

              {(() => {
                const primaryCta = buildPrimaryCta(proposal);
                if (primaryCta.isExternal) {
                  return (
                    <Box
                      bg={t.heroStart}
                      color="white"
                      borderRadius="32px"
                      p={{ base: 8, md: 10 }}
                      textAlign="center"
                    >
                      <Stack spacing={4} align="center">
                        <Heading as="h2" size="xl" fontWeight="900">
                          Scopri questa proposta in dettaglio
                        </Heading>
                        <HStack spacing={3} flexWrap="wrap" justify="center">
                          <Button
                            as="a"
                            href={primaryCta.href}
                            target="_blank"
                            rel="noopener noreferrer"
                            bg={t.accent}
                            color={t.text}
                            borderRadius="999px"
                            px={8}
                            py={6}
                            fontWeight="900"
                          >
                            {primaryCta.label}
                          </Button>
                          <Button
                            onClick={handleShare}
                            variant="outline"
                            borderColor={t.accent}
                            color={t.accent}
                            borderRadius="999px"
                            px={6}
                            py={6}
                            fontWeight="800"
                          >
                            {shareCopied ? 'Link copiato' : 'Condividi'}
                          </Button>
                        </HStack>
                      </Stack>
                    </Box>
                  );
                }
                return (
                  <Box
                    bg={t.heroStart}
                    color="white"
                    borderRadius="32px"
                    p={{ base: 8, md: 10 }}
                    textAlign="center"
                  >
                    <Stack spacing={4} align="center">
                      <Heading as="h2" size="xl" fontWeight="900">
                        Scopri questa proposta in dettaglio
                      </Heading>
                      <HStack spacing={3} flexWrap="wrap" justify="center">
                        <Button
                          as={RouterLink}
                          to={primaryCta.to}
                          bg={t.accent}
                          color={t.text}
                          borderRadius="999px"
                          px={8}
                          py={6}
                          fontWeight="900"
                        >
                          {primaryCta.label}
                        </Button>
                        <Button
                          onClick={handleShare}
                          variant="outline"
                          borderColor={t.accent}
                          color={t.accent}
                          borderRadius="999px"
                          px={6}
                          py={6}
                          fontWeight="800"
                        >
                          {shareCopied ? 'Link copiato' : 'Condividi'}
                        </Button>
                      </HStack>
                    </Stack>
                  </Box>
                );
              })()}
            </Stack>
          ) : null}
        </Stack>
      </Container>
    </Box>
  );
}
